import { useState, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { createPromotionCheckout, listMyPromotions } from "@/lib/promotions.functions";
import { PROMOTION_PACKAGES } from "@/lib/promotions/packages";
import { formatGBP } from "@/lib/format";

export type PromoteListingDialogProps = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  auctionId: string;
  auctionStatus?: string | null;
  auctionEndTime?: string | null;
};

export function PromoteListingDialog({
  open,
  onOpenChange,
  auctionId,
  auctionStatus,
  auctionEndTime,
}: PromoteListingDialogProps) {
  const [days, setDays] = useState<3 | 7 | 14>(7);
  const qc = useQueryClient();
  const createCheckout = useServerFn(createPromotionCheckout);
  const list = useServerFn(listMyPromotions);

  const { data: promos } = useQuery({
    queryKey: ["listing-promotions", auctionId],
    queryFn: () => list({ data: { auctionId } }),
    enabled: open,
  });

  const active = useMemo(
    () =>
      (promos ?? []).find(
        (p) => p.status === "active" && p.expires_at && new Date(p.expires_at) > new Date(),
      ) ?? null,
    [promos],
  );

  const canPromote =
    auctionStatus === "active" &&
    (!auctionEndTime || new Date(auctionEndTime) > new Date());

  const buy = useMutation({
    mutationFn: async () => {
      const res = await createCheckout({
        data: { auctionId, packageDays: days, origin: window.location.origin },
      });
      if (!res.url) throw new Error("No checkout URL returned");
      window.location.assign(res.url);
      return res;
    },
    onError: (e) => toast.error((e as Error).message),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["listing-promotions", auctionId] }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-gold" />
            Promote listing
          </DialogTitle>
          <DialogDescription>
            Boost this listing across the marketplace. Featured listings appear in a
            dedicated homepage section and rank above ordinary listings in browse
            results.
          </DialogDescription>
        </DialogHeader>

        {active && (
          <div className="rounded-md border border-gold/40 bg-gold/5 p-3 text-sm">
            <div className="font-medium text-gold">Currently promoted</div>
            <div className="text-xs text-muted-foreground mt-1">
              Expires {new Date(active.expires_at!).toLocaleString("en-GB")}
              {active.starts_at
                ? ` · started ${new Date(active.starts_at).toLocaleString("en-GB")}`
                : ""}
              . Buying another package extends the featured window.
            </div>
          </div>
        )}

        {!canPromote && (
          <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-sm text-amber-300">
            Only active, in-progress listings can be promoted.
          </div>
        )}

        <div className="grid grid-cols-3 gap-2">
          {PROMOTION_PACKAGES.map((p) => (
            <button
              key={p.days}
              type="button"
              onClick={() => setDays(p.days)}
              className={`rounded-lg border p-3 text-left transition-colors ${
                days === p.days
                  ? "border-gold bg-gold/10"
                  : "border-border/60 hover:border-gold/50"
              }`}
            >
              <div className="text-xs uppercase tracking-widest text-muted-foreground">
                {p.label}
              </div>
              <div className="mt-1 font-display text-2xl text-gold">
                {formatGBP(p.amount_pence)}
              </div>
            </button>
          ))}
        </div>

        <p className="text-[11px] text-muted-foreground">
          Payments are processed by Stripe. The promotion activates only after payment
          is confirmed. Cancelling checkout does not charge you.
        </p>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            className="bg-gold-gradient hover:opacity-90"
            disabled={!canPromote || buy.isPending}
            onClick={() => buy.mutate()}
          >
            {buy.isPending ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirecting…</>
            ) : (
              <>Promote for {PROMOTION_PACKAGES.find((p) => p.days === days)?.label}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
